import { cancelFlow_offerResultClick, OfferTypes } from 'analytics/trackers/cancelFlow';
import ReturnOfferContainer from 'containers/Offers/ReturnOffer';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ModalName } from 'store/modals/types';
import { returnSubscription } from 'store/subscriptions/actions';
import { getAppropriateDiscountForSubscription } from 'store/subscriptions/helpers';
import { selectDiscountedSubscriptions, selectMainSubscription } from 'store/subscriptions/selectors';

import { DISCOUNT_TYPES } from 'constants/subscriptions';

import { useDispatch, useReturnOfferPageProtection, useSelector, useSubscriptions } from 'hooks';
import { useModals } from 'hooks/modals';
import { useDiscountProduct } from 'hooks/subscriptions';

import { getSubscriptionsPageUrl } from 'helpers/pathes';
import { isEmpty } from 'helpers/utils';

import ReturnOfferSkeleton from 'components/RetrunOfferSkeleton/ReturnOfferSkeleton';

import { Product } from 'types/product';
import { IDiscountsForSubscriptions, IDiscountSubscription, ISubscription, PaymentProviders } from 'types/subscription';

export const ReturnOffer = () => {
    const { subscriptions } = useSubscriptions();

    useDiscountProduct(subscriptions);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { openModal, closeModal } = useModals();
    const { t } = useTranslation();
    const [isLoading, setLoadingState] = useState(false);

    useReturnOfferPageProtection();

    const discountedSubscriptions: IDiscountsForSubscriptions = useSelector(selectDiscountedSubscriptions);
    const subscription: ISubscription | undefined = useSelector(selectMainSubscription);

    const specialOfferProduct: IDiscountSubscription | undefined = getAppropriateDiscountForSubscription(
        subscription as ISubscription,
        discountedSubscriptions,
        DISCOUNT_TYPES.extra
    );

    const oldProduct = subscription?.product;
    const isLoadingOffer = isEmpty(specialOfferProduct);

    const handleRetrySubmit = () => {
        closeModal();
        handleSubmit();
    };

    const handleSubmit = () => {
        setLoadingState(true);

        dispatch(
            returnSubscription({
                id: subscription?.external_id as string,
                product: specialOfferProduct as IDiscountSubscription,
                discount_type: DISCOUNT_TYPES.extra,
                provider: subscription?.payment_provider as PaymentProviders,
                onError: () => {
                    setLoadingState(false);
                    openModal(ModalName.universalModal, {
                        image: 'errorAttention',
                        onClose: () => {
                            cancelFlow_offerResultClick({
                                action: 'close',
                                result: 'fail',
                                offer_type: OfferTypes.Return_offer,
                                subscription_id: subscription?.external_id,
                            });

                            closeModal();
                        },
                        onPrimaryClick: () => {
                            cancelFlow_offerResultClick({
                                action: 'try_again',
                                result: 'fail',
                                offer_type: OfferTypes.Return_offer,
                                subscription_id: subscription?.external_id,
                            });
                            handleRetrySubmit();
                        },
                        onContactUsClick: () =>
                            cancelFlow_offerResultClick({
                                action: 'contact_support',
                                result: 'fail',
                                offer_type: OfferTypes.Return_offer,
                                subscription_id: subscription?.external_id,
                            }),
                        withCloseButton: true,
                        withContactUsLink: true,
                        captions: {
                            title: t('basics.somethingWentWrong'),
                            primaryButton: t('basics.tryAgain'),
                            tertiaryButton: t('basics.contactSupport'),
                        },
                        dataLocators: {
                            modal: 'cancelSubModal',
                            container: 'updatingSubscriptionPlanModal',
                            primaryButton: 'errorModalTryAgain',
                            closeButton: 'errorModalCloseButton',
                        },
                    });
                },
                onSuccess: () => {
                    setLoadingState(false);
                    navigate(getSubscriptionsPageUrl());
                },
            })
        );
    };

    return (
        <>
            {isLoadingOffer ? (
                <ReturnOfferSkeleton />
            ) : (
                <ReturnOfferContainer
                    handleSubmit={handleSubmit}
                    subscription={subscription as ISubscription}
                    oldProduct={oldProduct as Product}
                    specialOfferProduct={specialOfferProduct as IDiscountSubscription}
                    isLoading={isLoading}
                />
            )}
        </>
    );
};
